import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  MeasurementsResponseMetrics,
  MeasurementsResponseMetricsUnits,
} from 'src/app/analyses/interfaces/analyses.types';
import { AnalysisId } from 'src/app/analyses/interfaces/analysis-id.enum';
import { AnalyticsFormKey } from 'src/app/analyses/interfaces/analytics-form-key.interface';
import { BodyFatCircumferenceInstructionsModalComponent } from 'src/app/analyses/modals/bf-circumference-instructions/bf-circumference-instructions.modal';
import { MeasurementAddBaseDirective } from 'src/app/body-measurements/directives/measurement-add-base.directive';
import { MeasurementAddBase } from 'src/app/body-measurements/interfaces/measurement-add.interface';
import { inputPattern } from 'src/app/body-measurements/validators/pattern.validator';

//This adds `meta` to form control in this component
declare module '@angular/forms' {
  // eslint-disable-next-line no-unused-vars
  interface FormControl {
    meta: any;
  }
}
FormControl.prototype.meta = {};

const getControlName = (analysisId: string, unitId: string) => {
  return `${analysisId}|${unitId}`;
};

@Component({
  selector: 'app-measurement-add-body-fat-manual',
  templateUrl: './measurement-add-body-fat-manual.component.html',
  styleUrls: ['./measurement-add-body-fat-manual.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasurementAddBodyFatManualComponent
  extends MeasurementAddBaseDirective
  implements MeasurementAddBase {
  protected form = this.fb.record({});

  protected isFemale = false;
  protected AnalysisId = AnalysisId;

  constructor(private fb: FormBuilder, private modals: NgbModal) {
    super();
  }

  init(): void {
    this.isFemale = this.configuration.id === AnalysisId.FatPercentageFemale;

    this.addControls();

    super.init();
  }

  onSubmit() {
    if (this.form.invalid) {
      return;
    }

    const measurements = this.controlArray.map((control) => {
      return {
        id: control.meta.metric.id,
        unitId: control.meta.unit.id,
        value: control.value,
      };
    });

    return {
      measurements,
      generated: new Date().toISOString(),
    };
  }

  public trackInput(event: any, inputId: string): void {
    this.analyticsService.trackFormValue(
      this.formName,
      AnalyticsFormKey.value,
      { value: this.form.get(event.target.id).value },
      this.configuration.id,
      inputId
    );
  }

  protected fillForm(): void {
    if (this.detailedResult.measurements.length === 1) {
      return;
    }

    this.detailedResult.measurements.forEach((measurement) => {
      this.form
        .get(getControlName(measurement.measurementId, measurement.unitId))
        .patchValue(String(measurement.value));
    });
  }

  protected openModal() {
    this.modals.open(BodyFatCircumferenceInstructionsModalComponent, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'tall-modal',
      backdropClass: 'basic-modal-backdrop',
    });
  }

  protected getMeasurementByIndex(idx: number): MeasurementsResponseMetrics {
    return this.configuration.measurementMetrics[idx];
  }

  protected getUnitByIndex(
    measurementIndex: number,
    unitIndex: number
  ): MeasurementsResponseMetricsUnits {
    return this.getMeasurementByIndex(measurementIndex).units[unitIndex];
  }

  protected updateValidators() {
    this.controlArray.forEach((control) => {
      control.addValidators([
        Validators.required,
        Validators.min(control.meta.unit.min),
        Validators.max(control.meta.unit.max),
        Validators.pattern(inputPattern(control.meta.unit.decimalPlaces)),
      ]);
    });
  }

  protected get controlArray(): FormControl[] {
    return Object.keys(this.form.controls).map(
      (controlName) => this.form.get(controlName) as FormControl
    );
  }

  private addControls() {
    this.configuration.measurementMetrics.slice(1).forEach((metric) => {
      metric.units.forEach((unit, index) => {
        const name = getControlName(metric.id, unit.id);
        const meta = {
          metric,
          unit,
          halfWidth: metric.units.length > 1,
          unitIndex: index,
        };
        const control = this.fb.control('');
        control.meta = meta;
        this.form.addControl(name, control);
      });
    });
  }
}
